import React, { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from '@mui/material';
import { SupplierGroup, SupplierGroupPayload } from '../../../../models/supplierAdministration';

type SupplierGroupDialogProps = {
	open: boolean;
	group: SupplierGroup | null;
	onClose: () => void;
	onSubmit: (payload: SupplierGroupPayload) => Promise<void>;
}

const SupplierGroupDialog = ({ open, group, onClose, onSubmit }: SupplierGroupDialogProps) => {
	const [name, setName] = useState('');
	const [code, setCode] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		setName(group?.name || '');
		setCode(group?.code || '');
		setError(null);
	}, [group, open]);

	const validate = () => {
		if (!name.trim()) return 'Le nom est obligatoire.';
		if (code.trim().length !== 2) return 'Le code doit contenir exactement 2 caractères.';
		return null;
	};

	const submit = async () => {
		const validationError = validate();
		if (validationError) {
			setError(validationError);
			return;
		}

		setLoading(true);
		setError(null);
		try {
			await onSubmit({
				name: name.trim(),
				code: code.trim().toUpperCase(),
			});
			onClose();
		} catch (error: any) {
			setError(error?.message || 'Une erreur est survenue.');
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>{group ? 'Modifier le groupe fournisseur' : 'Créer un groupe fournisseur'}</DialogTitle>
			<DialogContent>
				<Grid container spacing={2} sx={{ mt: 0.5 }}>
					{error && (
						<Grid size={12}>
							<Alert severity="error">{error}</Alert>
						</Grid>
					)}
					<Grid size={{ xs: 12, md: 8 }}>
						<TextField
							label="Nom"
							value={name}
							onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
							fullWidth
							required
							inputProps={{ maxLength: 150 }}
						/>
					</Grid>
					<Grid size={{ xs: 12, md: 4 }}>
						<TextField
							label="Code"
							value={code}
							onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCode(event.target.value.toUpperCase())}
							fullWidth
							required
							inputProps={{ maxLength: 2 }}
							helperText="2 caractères"
						/>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" color="secondary" onClick={onClose} disabled={loading}>
					Annuler
				</Button>
				<Button variant="contained" onClick={submit} disabled={loading}>
					Enregistrer
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default SupplierGroupDialog;
